import { useEffect } from 'react';
import { X } from 'lucide-react';
import { motion } from 'framer-motion';
import { createPortal } from 'react-dom';
import { useAuthStore } from '../stores/useAuthStore';

interface AuthModalProps {
  onClose: () => void;
}

function DiscordIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

export default function AuthModal({ onClose }: AuthModalProps) {
  const { signInWithTwitter, signInWithDiscord, loading, error, user } = useAuthStore();

  // Close the modal once the user is signed in.
  useEffect(() => {
    if (user) onClose();
  }, [user, onClose]);

  return createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-slate-800/90 backdrop-blur-lg rounded-xl p-6 max-w-md w-full border border-white/10"
      >
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-2xl font-bold">Log In to 2Players</h2>
          <button
            onClick={onClose}
            className="text-gray-300 hover:text-white transition"
            aria-label="Close"
          >
            <X size={24} />
          </button>
        </div>

        <p className="text-gray-300 text-sm mb-6">
          Sign in with Discord or X — your username carries over as your player name.
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/50 rounded-lg text-red-500 text-sm">
            {error}
          </div>
        )}

        <div className="space-y-3">
          <button
            type="button"
            onClick={signInWithDiscord}
            disabled={loading}
            className="w-full px-4 py-3 bg-[#5865F2] hover:bg-[#4752c4] rounded-lg font-medium transition flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <DiscordIcon />
            Continue with Discord
          </button>

          <button
            type="button"
            onClick={() => { signInWithTwitter().catch(() => { /* error shown in store */ }); }}
            disabled={loading}
            className="w-full px-4 py-3 bg-black hover:bg-neutral-800 border border-white/15 rounded-lg font-medium transition flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <XIcon />
            Continue with X
          </button>
        </div>

        <p className="text-xs text-center text-gray-400 mt-6">
          By continuing you agree to our{' '}
          <a href="/privacy-policy" className="text-pink-500 hover:text-pink-400 transition">
            Privacy Policy
          </a>
          .
        </p>
      </motion.div>
    </div>,
    document.body
  );
}
